import { Box } from '@mui/system';
import React, { FC, useEffect } from 'react';

import {
  AdminLegendVariant,
  PharmacistLegendVariant,
  adminResetValue,
  getPharmacyDetails,
  pharmacyDetails,
} from '@pharmaplan/common';

import PharmacyCard from '../../../SelfService/PharmacyCard';
import strings from '../../../../localization';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { ScreenTypes } from '../../../../helpers/Constants';
import { serverToday } from '../../../../helpers/Functions';

import {
  ICustomBack,
  renderScreen,
  setBack,
  setButtons,
  setFooterShow,
  setPagination,
} from '../../../../actions/drawerActions';
import useViewDetailsPharmacy from '../../../../hooks/Admin/useViewDetailsPharmacy';
import AdminHourlyRateFooter from '../../../common/AdminHourlyRateFooter';
import useGetItems from '../../../../hooks/useGetItems';
import { pharmacyProfileConfig } from '../../Profile/Pharmacy/PharmacyViewProfile/helper';
import useDetailsPagination from '../../../../hooks/Admin/useDetailsPagination';
import CollapsibleCard from '../../../common/CollapsibleCard';
import ProfileContent from '../../Profile/ProfileContent';

import styles from './styles';

interface IViewDetails {
  workshiftId: string;
  pharmacyId: string;
  type?: AdminLegendVariant;
  back?: ICustomBack;
  date?: string;
  isAutoSyncRecord?: boolean;
  bookingIdList?: Array<string> | null;
}

const ViewDetails: FC<IViewDetails> = ({
  workshiftId,
  pharmacyId,
  back,
  date,
  type = AdminLegendVariant.posted,
  isAutoSyncRecord,
  bookingIdList,
}) => {
  const dispatch = useAppDispatch();
  const { id, paginationModel } = useDetailsPagination(bookingIdList);
  const { details: event, api, successAction } = useViewDetailsPharmacy(type);
  const eventId = id ?? workshiftId;
  const isNotFromMap = !id;

  const {
    getImage,
    getLanguageName,
    getSoftwareName,
    getSelectedContactPreferences,
  } = useGetItems();

  const details = useAppSelector(pharmacyDetails);

  const isBooked = type === AdminLegendVariant.booked;
  const isRequested = type === AdminLegendVariant.requested;

  const { pharmacy, softwares, contactPreferences } = details ?? {};
  const { softwareId, selectedContactPreferences } = pharmacy ?? {};

  const { logoId, languageId, pharmacyHourlyRate, startDate, softwareName } = (event as any) ?? {};

  const avatar = getImage(logoId);
  const language = getLanguageName(languageId);
  const software = getSoftwareName(softwares, softwareId);
  const selectedPreferences = getSelectedContactPreferences(
    contactPreferences,
    selectedContactPreferences,
  );

  const isPast = (serverToday().toISO({ includeOffset: false }) ?? '') > startDate;

  const goToEditWorkshift = () => {
    dispatch(
      renderScreen({
        screenNumber: 2,
        screenType: ScreenTypes.availableWorkshift,
        eventId,
      }),
    );
  };

  const goToAvailableWorkshift = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.availableWorkshift,
        date,
      }),
    );
  };

  useEffect(() => {
    dispatch(api({ id: eventId, isAutoSyncRecord }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (pharmacyId) {
      dispatch(getPharmacyDetails({ id: pharmacyId, isAutoSyncRecord }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId]);

  useEffect(() => {
    if (!(isBooked || isRequested) && !isAutoSyncRecord) {
      dispatch(
        setButtons({
          show: true,
          primaryButton: {
            label: strings.editWorkshift,
            onClick: goToEditWorkshift,
          },
        }),
      );
    }

    if (back) {
      dispatch(setBack(back));
    } else if (date) {
      dispatch(setBack(goToAvailableWorkshift));
    } else {
      dispatch(setBack(null));
    }

    if (!isPast && !isNotFromMap) {
      dispatch(setPagination(paginationModel));
    } else {
      dispatch(setPagination({ show: false }));
    }

    dispatch(
      setFooterShow(!(isBooked || isRequested) && !isPast && !isAutoSyncRecord),
    );

    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue(['workshiftDetails']));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={styles.container}>
      <PharmacyCard
        hideBorder={isBooked}
        showPharmacyName
        type={PharmacistLegendVariant.workshift}
        CustomContent={isBooked ? <div style={styles.emptyDiv} /> : null}
        event={{
          ...event,
          hourlyRate: pharmacyHourlyRate,
          logoImage: avatar,
        }}
        isAutoSyncRecord={isAutoSyncRecord}
        successAction={successAction}
        CustomFooter={(
          <AdminHourlyRateFooter
            pharmacyHourlyRate={pharmacyHourlyRate}
            software={softwareName}
            successAction={successAction}
          />
        )}
      />
      {pharmacyProfileConfig({
        details,
        language,
        software,
        successAction,
        selectedPreferences,
        isAutoSyncRecord,
      }).map((item) => {
        const { key, title, keepExpanded, cardContent, customComponent } = item ?? {};

        return (
          <CollapsibleCard
            key={key}
            title={title}
            keepExpanded={keepExpanded}
            content={(
              <ProfileContent
                content={cardContent}
                customComponent={customComponent}
                successAction={successAction}
              />
            )}
          />
        );
      })}
    </Box>
  );
};

export default ViewDetails;
